import { useEffect, useState } from 'react';
import axios from 'axios';
import logger from '@/lib/logger';
import { Trophy, Flame, Barbell, Clock, Eye, EyeSlash, Handshake } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'streak', label: 'Streak', icon: Flame, metric: 'streak', suffix: 'days' },
  { id: 'workouts', label: 'All-Time', icon: Barbell, metric: 'workouts', suffix: 'sessions' },
  { id: 'this_week', label: 'This Week', icon: Clock, metric: 'minutes', suffix: 'min' },
  { id: 'friends', label: 'Friends', icon: Handshake, metric: 'streak', suffix: 'days' },
];

export default function LeaderboardsPage() {
  const [data, setData] = useState(null);
  const [friends, setFriends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('streak');
  const [toggling, setToggling] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [resp, fresp] = await Promise.all([
        axios.get(`${API}/leaderboards`),
        axios.get(`${API}/leaderboards/friends`).catch(() => ({ data: null })),
      ]);
      setData(resp.data);
      setFriends(fresp.data);
    } catch (err) {
      logger.error('Leaderboards load error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublic = async () => {
    if (!data) return;
    setToggling(true);
    try {
      const next = !data.me.is_public;
      await axios.put(`${API}/user/privacy`, { public_profile: next });
      await load();
    } catch (err) {
      logger.error('Privacy toggle error:', err);
    } finally {
      setToggling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center" data-testid="leaderboards-loading">
        <div className="w-8 h-8 border-2 border-[#FF3B30] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  const isFriendsTab = activeTab === 'friends';
  const activeMeta = TABS.find(t => t.id === activeTab);
  const rows = isFriendsTab
    ? (friends?.participants || []).slice().sort((a, b) => (b.streak || 0) - (a.streak || 0)).map((r, i) => ({ ...r, rank: i + 1 }))
    : (data[activeTab] || []);
  const myRank = isFriendsTab
    ? rows.find(r => r.is_me) || null
    : data.me?.[`${activeTab === 'this_week' ? 'this_week_rank' : activeTab + '_rank'}`];
  const isPublic = data.me?.is_public;

  return (
    <div data-testid="leaderboards-page">
      <div className="mb-8">
        <div className="label-tactical text-[#FF3B30] mb-1">Community</div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">LEADERBOARDS</h1>
        <p className="text-[#A1A1AA] text-sm mt-1">See how you stack up against the GRIT.FIT community</p>
      </div>

      {/* Privacy card */}
      <div className="tactical-card p-5 mb-6" data-testid="privacy-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className={`w-10 h-10 flex items-center justify-center ${isPublic ? 'bg-[#34C759]/10' : 'bg-[#71717A]/10'}`}>
            {isPublic ? <Eye weight="fill" className="w-5 h-5 text-[#34C759]" /> : <EyeSlash weight="fill" className="w-5 h-5 text-[#71717A]" />}
          </div>
          <div className="flex-1">
            <div className="font-bold text-sm tracking-tight">
              {isPublic ? 'You\'re on the public leaderboards' : 'Your profile is private'}
            </div>
            <p className="text-xs text-[#A1A1AA] mt-0.5">
              {isPublic
                ? 'Your name and fitness level appear to other users. Workouts and mood stay private.'
                : 'Go public to appear on the leaderboards and be counted against your peers.'}
            </p>
          </div>
          <button
            onClick={togglePublic}
            disabled={toggling}
            className={`${isPublic ? 'btn-secondary' : 'btn-primary'} px-5 py-2.5 text-sm whitespace-nowrap disabled:opacity-50`}
            data-testid="privacy-toggle-button"
          >
            {toggling ? '...' : isPublic ? 'Go Private' : 'Go Public'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4 overflow-x-auto" data-testid="leaderboard-tabs">
        {TABS.map(tab => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-2.5 min-h-[44px] text-sm font-bold uppercase tracking-wider transition-all whitespace-nowrap ${active ? 'bg-[#FF3B30] text-white' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'}`}
            >
              <tab.icon weight={active ? 'fill' : 'regular'} className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* My rank card */}
      {myRank && (
        <div className="tactical-card p-4 mb-4 border-[#FF3B30]/30" data-testid="my-rank">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FF3B30] flex items-center justify-center font-black font-['Barlow_Condensed'] text-lg">
              #{myRank.rank}
            </div>
            <div className="flex-1">
              <div className="font-bold tracking-tight">You — {myRank.name}</div>
              <div className="text-xs text-[#71717A] uppercase tracking-wider">{myRank.fitness_level}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black font-['Barlow_Condensed'] text-[#FF3B30]">{myRank[activeMeta.metric]}</div>
              <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{activeMeta.suffix}</div>
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard rows */}
      {(rows.length === 0 || (isFriendsTab && !friends?.has_buddy)) ? (
        <div className="tactical-card p-8 text-center" data-testid="leaderboard-empty">
          <Trophy weight="bold" className="w-12 h-12 text-[#71717A] mx-auto mb-3" />
          {isFriendsTab && !friends?.has_buddy ? (
            <>
              <p className="text-[#A1A1AA] font-bold">No accountability buddy yet</p>
              <p className="text-xs text-[#71717A] mt-2">Pair up with a buddy to compete side-by-side.</p>
              <a href="/buddy" className="inline-block mt-4 btn-primary px-5 py-2.5 text-sm" data-testid="find-buddy-link">Find a Buddy</a>
            </>
          ) : (
            <>
              <p className="text-[#A1A1AA] font-bold">Be the first to make the leaderboard!</p>
              <p className="text-xs text-[#71717A] mt-2">No public users yet. Go public above and complete a workout.</p>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-2" data-testid="leaderboard-list">
          {rows.map(row => {
            const isMe = row.is_me || row.user_id === data.me?.user_id;
            const medal = row.rank === 1 ? 'text-[#FFD700]' : row.rank === 2 ? 'text-[#C0C0C0]' : row.rank === 3 ? 'text-[#CD7F32]' : 'text-[#71717A]';
            return (
              <div
                key={row.user_id}
                data-testid={`leaderboard-row-${row.rank}`}
                className={`flex items-center gap-3 p-3 tactical-card ${isMe ? 'border-[#FF3B30]/50 bg-[#FF3B30]/5' : ''}`}
              >
                <div className={`w-10 font-black font-['Barlow_Condensed'] text-2xl text-center ${medal}`}>
                  {row.rank <= 3 ? <Trophy weight="fill" className="w-6 h-6 mx-auto" /> : `#${row.rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold tracking-tight truncate">{row.name}{isMe && <span className="text-[#FF3B30] ml-2 text-xs">(You)</span>}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{row.fitness_level}</div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-black font-['Barlow_Condensed']">{row[activeMeta.metric] ?? 0}</div>
                  <div className="text-[10px] text-[#71717A] uppercase tracking-wider">{activeMeta.suffix}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
